/**
 * Progressive-web-app plumbing: service worker registration, the deferred
 * install prompt, and the two platform sniffs the push UI needs. Kept free of
 * React so main.tsx can call it before anything renders.
 */

/**
 * Registers /public/sw.js. Production only: in dev Vite serves fresh modules
 * and a worker would only ever cache stale ones.
 */
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  const register = () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
  };
  // After load, so the worker's own fetches do not compete with the app bundle.
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

/** True when running as an installed app rather than in a browser tab. */
export function isStandalone(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  // Safari's pre-standard flag for Home Screen web apps.
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/** iPhone, iPod or iPad. iPadOS reports itself as a Mac, hence the touch check. */
export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

// ── Install prompt ───────────────────────────────────────────────────────────

/** Chromium's non-standard event; the DOM lib does not ship it. */
type BeforeInstallPromptEvent = Event & {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const installListeners = new Set<() => void>();

function notifyInstallListeners(): void {
  for (const listener of installListeners) listener();
}

window.addEventListener('beforeinstallprompt', (event) => {
  // Hold the browser's own banner back; the PushPrompt card offers install instead.
  event.preventDefault();
  deferredPrompt = event as BeforeInstallPromptEvent;
  notifyInstallListeners();
});

window.addEventListener('appinstalled', () => {
  deferredPrompt = null;
  notifyInstallListeners();
});

/**
 * The captured install prompt as a tiny external store, shaped for
 * useSyncExternalStore. `promptInstall` shows the browser's dialog and
 * resolves true when the user accepted.
 */
export const installPromptStore = {
  subscribe(listener: () => void): () => void {
    installListeners.add(listener);
    return () => {
      installListeners.delete(listener);
    };
  },
  getSnapshot(): boolean {
    return deferredPrompt !== null;
  },
  async promptInstall(): Promise<boolean> {
    const event = deferredPrompt;
    if (!event) return false;
    // The event is single-use; drop it before prompting so a double-click cannot prompt twice.
    deferredPrompt = null;
    notifyInstallListeners();
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome === 'accepted';
  },
};

// ── Messages from the worker ─────────────────────────────────────────────────

/** Sent by sw.js when a notification is clicked while a window is already open. */
export type ServiceWorkerMessage = { type: 'navigate'; link: string };

function isNavigateMessage(value: unknown): value is ServiceWorkerMessage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { type?: unknown; link?: unknown };
  return candidate.type === 'navigate' && typeof candidate.link === 'string';
}

/** Turns an absolute same-origin URL into a router path; anything else passes through. */
function toAppPath(link: string): string {
  try {
    const url = new URL(link, window.location.origin);
    if (url.origin === window.location.origin) return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    // Not a URL at all; let the router make sense of it.
  }
  return link;
}

/**
 * Subscribes to the worker's navigate message. The page owns routing (the
 * worker cannot see the router or the token), so the handler should call
 * navigate(message.link). Returns the unsubscribe function.
 */
export function onServiceWorkerMessage(
  handler: (message: ServiceWorkerMessage) => void,
): () => void {
  if (!('serviceWorker' in navigator)) return () => undefined;
  const listener = (event: MessageEvent<unknown>) => {
    if (isNavigateMessage(event.data)) {
      handler({ type: 'navigate', link: toAppPath(event.data.link) });
    }
  };
  navigator.serviceWorker.addEventListener('message', listener);
  return () => navigator.serviceWorker.removeEventListener('message', listener);
}
